import { defineAsyncComponent } from "vue";
import { imageNames } from "./imageConstants.js";

export const prStatuses = {
  DRAFT: "draft",
  OPEN: "open",
  CLOSED: "closed",
  MERGED: "merged",
  READY_FOR_REVIEW: "ready_for_review",
};

export const prReviewStates = {
  APPROVED: "approved",
  CHANGES_REQUESTED: "changes_requested",
  COMMENTED: "commented",
};

export const prMergeStrategies = {
  MERGE: "merge",
  SQUASH: "squash",
  REBASE: "rebase",
};

export const prCommentTypes = {
  GENERAL: "general",
  INLINE: "inline",
};

export const prMergeabilityPollIntervalMs = 10000;
export const prMergeabilityPollMaxErrors = 5;

export const codebaseTabs = [
  {
    id: "code",
    title: "Code",
    selected: imageNames.CODE,
    unselected: imageNames.CODE,
  },
  {
    id: "pull_requests",
    title: "Pull Requests",
    selected: imageNames.PULL_REQUESTS,
    unselected: imageNames.PULL_REQUESTS,
  },
  {
    id: "commits",
    title: "Commits",
    selected: imageNames.COMMITS,
    unselected: imageNames.COMMITS,
  },
  {
    id: "actions",
    title: "Actions",
    selected: imageNames.GIT_ACTIONS,
    unselected: imageNames.GIT_ACTIONS,
  },
  {
    id: "settings",
    title: "Settings",
    selected: imageNames.REPO_SETTINGS_UNSELECTED,
    unselected: imageNames.REPO_SETTINGS_UNSELECTED,
  },
];

export const codebaseTabIDs = {
  CODE: "code",
  PULL_REQUESTS: "pull_requests",
  COMMITS: "commits",
  ACTIONS: "actions",
  SETTINGS: "settings",
};

export const repositoryEvents = {
  CREATED: "created",
  UPDATED: "updated",
  DELETED: "deleted",
};

export const getRepositoryItemActions = (imageNames, handlers) => [
  {
    id: "more_options",
    title: "More options",
    icon: imageNames.MORE_ICON_GREY,
    size: 14,
    tooltip: true,
    options: [
      {
        id: "rename",
        title: "Rename",
        icon: imageNames.EDIT,
        size: 14,
        action: handlers.onRename,
      },
      {
        id: "setup_instructions",
        title: "Setup Instructions",
        icon: imageNames.INFO_GREY,
        size: 14,
        action: handlers.onGetSetupInstructions,
      },
      {
        id: "delete",
        title: "Delete",
        icon: imageNames.DELETE_GREY,
        size: 14,
        action: handlers.onDelete,
      },
    ],
  },
];

export const codeColumnKeys = {
  NAME: "name",
};

export const codeNodeTypes = {
  FOLDER: "folder",
  FILE: "file",
  TREE: "tree",
  BLOB: "blob",
};

export const commitFileStatuses = {
  ADDED: "added",
  MODIFIED: "modified",
  DELETED: "deleted",
  RENAMED: "renamed",
};

export const codeTableHeaders = [
  { key: "name", title: "Source", width: 300 },
  { key: "lastActivity", title: "Last activity", width: 200 },
  { key: "lastActivityDate", title: "Last activity date", width: 200 },
];

export const actionsRunTableHeaders = [
  {
    label: "Title",
    key: "workflow_info",
    width: 400,
  },
  {
    label: "Branch",
    key: "head_branch",
    width: 140,
  },
  {
    label: "Status",
    key: "status",
    width: 120,
  },
  {
    label: "Created at",
    key: "created_at",
    width: 160,
  },
];

export const workflowTableHeaders = [
  { label: "Name", key: "name", width: 300 },
  { label: "File path", key: "file_path", width: 300 },
  { label: "Last updated", key: "updated_at", width: 160 },
];

export const runDetailTabs = [
  { id: "activity", title: "Activity" },
  { id: "workflow", title: "Workflow" },
  { id: "jobs", title: "Jobs" },
  { id: "usage", title: "Usage" },
  { id: "file", title: "File" },
];

export const runDetailTabIds = {
  ACTIVITY: "activity",
  WORKFLOW: "workflow",
  JOBS: "jobs",
  USAGE: "usage",
  FILE: "file",
};

export const usageTableHeaders = [
  {
    label: "Job",
    key: "job_name",
    width: 400,
  },
  {
    label: "Run time",
    key: "run_time",
    width: 200,
  },
];

export const pullRequestsTableHeaders = [
  {
    label: "ID",
    key: "number_display",
    width: 80,
  },
  {
    label: "Title",
    key: "title",
    width: 350,
  },
  {
    label: "Status",
    key: "status",
    width: 120,
  },
  {
    label: "Contributors",
    key: "contributor_name",
    width: 160,
    avatar: "profile_pic",
    avatarParam: "name",
    avatarDataKey: "contributor_data",
    multiType: "avatarGroup",
  },
  {
    label: "Last Activity",
    key: "last_activity",
    width: 160,
  },
];

export const pullRequestTabIDs = {
  ACTIVITY: "activity",
  COMMENTS: "comments",
  COMMITS: "commits",
  FILES_CHANGED: "files_changed",
  RULE_ALERTS: "rule_alerts",
};

export const pullRequestTabs = [
  { id: pullRequestTabIDs.ACTIVITY, title: "Activity" },
  { id: pullRequestTabIDs.COMMENTS, title: "Comments" },
  { id: pullRequestTabIDs.RULE_ALERTS, title: "Rule Alerts" },
  { id: pullRequestTabIDs.COMMITS, title: "Commits" },
  { id: pullRequestTabIDs.FILES_CHANGED, title: "Files changed" },
];

export const actionDetailTabIDs = {
  ACTIVITY: "activity",
  WORKFLOW: "workflow",
  JOBS: "jobs",
  USAGE: "usage",
  FILE: "file",
};

export const actionDetailTabs = [
  { id: actionDetailTabIDs.ACTIVITY, title: "Activity" },
  { id: actionDetailTabIDs.WORKFLOW, title: "Workflow" },
  { id: actionDetailTabIDs.JOBS, title: "Jobs" },
  { id: actionDetailTabIDs.USAGE, title: "Usage" },
  { id: actionDetailTabIDs.FILE, title: "File" },
];

export const prReviewStateConfig = {
  [prReviewStates.APPROVED]: { label: "Approved", color: "#22c55e" },
  [prReviewStates.CHANGES_REQUESTED]: {
    label: "Changes requested",
    color: "#f97316",
  },
  [prReviewStates.COMMENTED]: { label: "Commented", color: "#6b7280" },
};

export const prActivityEventLabels = {
  commit: "Committed",
  comment: "Comment",
  commented: "Comment",
  created: "Opened PR",
  merged: "Merged",
  closed: "Closed",
  reviewed: "Reviewed",
  draft: "Draft",
  reviewer_added: "Reviewer added",
  reviewer_removed: "Reviewer removed",
};

export const prActivityTypeOptions = [
  { label: "Opened PR", value: "created", types: ["created"] },
  { label: "Committed", value: "commit", types: ["commit"] },
  { label: "Commented", value: "commented", types: ["commented", "comment"] },
  { label: "Reviewed", value: "reviewed", types: ["reviewed"] },
  { label: "Merged", value: "merged", types: ["merged"] },
  { label: "Closed", value: "closed", types: ["closed"] },
  {
    label: "Reviewer added",
    value: "reviewer_added",
    types: ["reviewer_added"],
  },
  {
    label: "Reviewer removed",
    value: "reviewer_removed",
    types: ["reviewer_removed"],
  },
];

export const getPRActivityLabel = (eventType) =>
  prActivityEventLabels[eventType] || eventType;

export const getPRReviewStateBadgeClass = (state) => {
  if (state === prReviewStates.APPROVED)
    return "bg-green-500/20 text-green-400";
  if (state === prReviewStates.CHANGES_REQUESTED)
    return "bg-red-500/20 text-red-400";
  return "bg-[var(--white-opacity-8)] text-[var(--secondary-color)]";
};

export const prReviewOptions = [
  {
    value: prReviewStates.COMMENTED,
    label: "Comment",
    color: "#6b7280",
    desc: "Submit general feedback without explicit approval.",
  },
  {
    value: prReviewStates.APPROVED,
    label: "Approve",
    color: "#22c55e",
    desc: "Submit feedback and approve merging these changes.",
  },
  {
    value: prReviewStates.CHANGES_REQUESTED,
    label: "Request changes",
    color: "#f97316",
    desc: "Submit feedback that must be addressed before merging.",
  },
];

export const personalTokensTableHeaders = [
  { key: "name", label: "Name", width: 220 },
  { key: "created_at", label: "Created At", width: 180 },
  { key: "expires_at", label: "Expires At", width: 180 },
  { key: "last_used_at", label: "Last Used At", width: 180 },
];

export const sshKeysTableHeaders = [
  { key: "name", label: "SSH Key Name", width: 220 },
  { key: "key_type", label: "Key Type", width: 200 },
  { key: "fingerprint", label: "Fingerprint", width: 340 },
  { key: "created_at", label: "Added On", width: 180 },
];

export const sshKeyTypes = [
  { value: "authentication", label: "Authentication Key" },
  { value: "signing", label: "Signing Key" },
];

export const prStrategyLabels = {
  [prMergeStrategies.MERGE]: "Merge pull request",
  [prMergeStrategies.SQUASH]: "Squash and merge",
  [prMergeStrategies.REBASE]: "Rebase and merge",
};

export const gitInstructionTexts = {
  TITLE: "Quick setup — if you've done this kind of thing before",
  SUB_TITLE: "Get started by pushing your code to this repository",
};

export const addFileOptions = [
  { id: "upload", text: "Upload file", icon: imageNames.UPLOAD_CSV_GREY },
  { id: "create", text: "Create new file", icon: imageNames.RENAME },
];

export const codeViews = {
  LIST: "list",
  DIR: "dir",
  FILE: "file",
  CREATE: "create",
};

export const repoSettingEntities = {
  GENERAL: "general",
  BRANCHES: "branches",
  RULESETS: "rulesets",
  WEBHOOKS: "webhooks",
};

export const repoSettingOptions = {
  [repoSettingEntities.GENERAL]: {
    id: repoSettingEntities.GENERAL,
    name: "General",
    component: defineAsyncComponent(
      () => import("@/src/views/codebase/settings/General.vue"),
    ),
    selected: imageNames.REPO_SETTINGS_SELECTED,
    unselected: imageNames.REPO_SETTINGS_UNSELECTED,
  },
  [repoSettingEntities.BRANCHES]: {
    id: repoSettingEntities.BRANCHES,
    name: "Branches",
    component: defineAsyncComponent(
      () => import("@/src/views/codebase/settings/Branches.vue"),
    ),
    selected: imageNames.REPO_BRANCHES_SELECTED,
    unselected: imageNames.REPO_BRANCHES_UNSELECTED,
  },
  [repoSettingEntities.RULESETS]: {
    id: repoSettingEntities.RULESETS,
    name: "Rulesets",
    component: defineAsyncComponent(
      () => import("@/src/views/codebase/settings/Rulesets.vue"),
    ),
    selected: imageNames.REPO_RULESETS_SELECTED,
    unselected: imageNames.REPO_RULESETS_UNSELECTED,
  },
  [repoSettingEntities.WEBHOOKS]: {
    id: repoSettingEntities.WEBHOOKS,
    name: "Webhooks",
    component: defineAsyncComponent(
      () => import("@/src/views/codebase/settings/Webhooks.vue"),
    ),
    selected: imageNames.REPO_WEBHOOKS_SELECTED,
    unselected: imageNames.REPO_WEBHOOKS_UNSELECTED,
  },
};

export const branchProtectionViolationTypes = {
  outOfDate: "out_of_date",
  missingApprovals: "missing_approvals",
  staleReviews: "stale_reviews",
  failingChecks: "failing_checks",
  unresolvedConversations: "unresolved_conversations",
  linearHistory: "linear_history",
  locked: "locked_branch",
};

export const prViolationTypes = {
  OUT_OF_DATE: "out_of_date",
  MISSING_APPROVALS: "missing_approvals",
  STALE_REVIEWS: "stale_reviews",
  FAILING_CHECKS: "failing_checks",
  LOCKED_BRANCH: "locked_branch",
  LINEAR_HISTORY: "linear_history",
  UNRESOLVED_CONVERSATIONS: "unresolved_conversations",
};

export const webhookContentTypes = [
  { value: "application/json", label: "application/json" },
  {
    value: "application/x-www-form-urlencoded",
    label: "application/x-www-form-urlencoded",
  },
];

export const webhookEvents = [
  {
    value: "push",
    label: "Push",
    description: "Any push to a repository branch.",
  },
  {
    value: "pull_request",
    label: "Pull request",
    description: "Pull request opened, closed, or synchronized.",
  },
  {
    value: "branch_protection_rule",
    label: "Branch protection rule",
    description: "Branch protection rule created, edited, or deleted.",
  },
  { value: "status", label: "Status", description: "Commit status updated." },
  { value: "*", label: "Send me everything", description: "All events." },
];

export const terminalRunStatuses = [
  "completed",
  "failed",
  "cancelled",
  "timed_out",
];
export const terminalJobStatuses = [
  "completed",
  "failed",
  "cancelled",
  "skipped",
  "timed_out",
];
